import { useState } from 'react';

import { shareLinksService } from '../services';
import { cn } from '../ui/cn';
import { Button } from '../ui/primitives';
import { buildShareUrl, type ShareDocumentKind } from './share-link';
import { buildWhatsAppShareUrl, normalizeWhatsAppPhone } from './whatsapp';

/**
 * "Send via WhatsApp" — ONE component, four surfaces.
 *
 * A DEEP LINK, NOT A SEND. Pressing this mints a share link on the server, then
 * opens WhatsApp Web/App in a new tab with the message pre-filled. THE STAFF
 * MEMBER PRESSES SEND. Nothing leaves the building on its own, there is no
 * WhatsApp Business API behind it, and no message is delivered by this app. The
 * button copy says "Send via WhatsApp" because that is the task the user is
 * doing; the tooltip says what actually happens, because a control that appears
 * to have already sent a customer's invoice is a control people will not press
 * twice.
 *
 * WHAT THE CUSTOMER GETS is a bearer URL: anyone holding it can read that
 * document until it expires or is revoked. That is inherent to putting a link in
 * a WhatsApp message — messages get forwarded — and is the accepted, disclosed
 * tradeoff, not an oversight. The server bounds it with an expiry and with
 * revocation; this component does not pretend otherwise anywhere in its copy.
 */

/**
 * Why a number cannot be used, in the words the staff member needs.
 *
 * Phrased against the DOCUMENT rather than the customer on purpose: every phone
 * this reads is the document's own snapshot (`invoice.customerPhone`,
 * `voucher.guestPhone`, ...), so "no phone on this voucher" points at the field
 * they can actually go and fix, whereas "no phone for this customer" would send
 * them to a customer record that may well have one.
 */
function unusableReason(reason: 'missing' | 'malformed', label: string): string {
  const subject = label.toLowerCase();
  return reason === 'missing'
    ? `No phone number on this ${subject}, so there is nobody to send it to. Add one to the ${subject} first.`
    : `The phone number on this ${subject} needs a 10-digit mobile number, so WhatsApp would not reach it.`;
}

export interface WhatsAppShareButtonProps {
  /** Which family of `/share` routes to post to. */
  kind: ShareDocumentKind;
  /**
   * The document's id or its human ref — the share routes accept either, and
   * resolve it to the canonical row before storing anything.
   */
  documentId: string;
  /**
   * The document's name IN THE MESSAGE, e.g. "Invoice", "Voucher", "Credit
   * Note", "Receipt". Sentence-cased as the customer will read it, and lowercased
   * by `unusableReason` for the staff-facing failure copy.
   */
  label: string;
  /**
   * The document's own phone snapshot. `null`/`undefined`/`""` are all normal —
   * they render a disabled button with a reason rather than a broken link.
   */
  phone: string | null | undefined;
  /**
   * The document's file name, e.g. "INV-2607-0001.pdf".
   *
   * NOT sent to the server: the share routes resolve the document themselves and
   * derive the stored filename from the canonical row, so a client-supplied one
   * would be a second source of truth for a name the customer sees on a PDF. It
   * is used HERE for the button's accessible name, so a screen-reader user
   * hears WHICH document a row of otherwise identical buttons would share.
   */
  fileName: string;
  /** Merged onto the button, for the `ml-auto`/spacing each surface needs. */
  className?: string;
}

export function WhatsAppShareButton({
  kind,
  documentId,
  label,
  phone,
  fileName,
  className,
}: WhatsAppShareButtonProps): JSX.Element {
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const normalized = normalizeWhatsAppPhone(phone);

  /**
   * The document's name for assistive tech, from `fileName` minus its extension.
   *
   * On the ledger and the document cards there are several of these buttons on
   * one screen and "Send via WhatsApp" alone would be four identical accessible
   * names.
   */
  const documentName = fileName.replace(/\.pdf$/i, '');

  /**
   * DISABLED, WITH THE REASON ON IT.
   *
   * Not hidden: a missing phone number is a fixable data problem, and hiding the
   * control would leave the staff member with no hint that sharing is possible
   * at all. Not enabled-and-failing either — an enabled button here would build
   * a `wa.me/` URL with no subscriber in it, which opens WhatsApp on a blank
   * "invalid number" screen after the API call has already minted a live share
   * link for a document nobody received.
   */
  if (!normalized.ok) {
    const reason = unusableReason(normalized.reason, label);
    return (
      <Button variant="outline" className={className} disabled title={reason} aria-label={reason}>
        Send via WhatsApp
      </Button>
    );
  }

  const { e164 } = normalized;

  return (
    <>
      <Button
        variant="outline"
        className={className}
        disabled={sharing}
        aria-label={`Send ${documentName} via WhatsApp`}
        // The control states what it does, because "Send" on a button next to a
        // customer's invoice reads as "this has gone out" — and it has not.
        title="Creates a shareable link and opens WhatsApp with the message ready. You press Send."
        onClick={() => {
          setSharing(true);
          setShareError(null);
          void shareLinksService
            .create(kind, documentId)
            .then(({ token }) => {
              const message = `Here's your ${label} from Guruji Travel Yatra: ${buildShareUrl(token)}`;
              /**
               * `noopener,noreferrer` — the opened tab must not get a handle on
               * this one via `window.opener`.
               *
               * A null return means the browser blocked the popup. It is worth
               * catching: the share link HAS been minted by this point, so
               * silently doing nothing would leave a live bearer link for a
               * document the customer never received, and the staff member with
               * a button that looked broken.
               */
              const opened = window.open(
                buildWhatsAppShareUrl(e164, message),
                '_blank',
                'noopener,noreferrer',
              );
              if (opened === null) {
                setShareError(
                  'The link was created, but your browser blocked the WhatsApp tab from opening. Allow pop-ups for this site and try again.',
                );
              }
            })
            .catch((err: unknown) => {
              setShareError(
                err instanceof Error ? err.message : 'Could not create the share link.',
              );
            })
            .finally(() => {
              setSharing(false);
            });
        }}
      >
        {sharing ? 'Preparing…' : 'Send via WhatsApp'}
      </Button>

      {/*
        Inline and `basis-full`, not a toast: every surface that hosts this
        button is a `flex flex-wrap` row, so the message drops onto its own line
        directly under the control that produced it — and it is still there when
        the user looks back, which a faded toast is not.
      */}
      {shareError !== null && (
        <p
          role="alert"
          className={cn(
            'w-full basis-full rounded-[8px] border border-danger-border bg-danger-bg',
            'px-3 py-2 text-[11.5px] leading-[1.5] text-danger-ink',
          )}
        >
          {shareError}
        </p>
      )}
    </>
  );
}
